<template>
  <div class="trip-review">
    <slot></slot>
    <b-tabs v-model="activeTab">
      <b-tab-item label="Flight breakdown">
        <div class="flight-info">
          <b-message type="is-primary" class="no-margin-bottom no-border">
            <span class="price-item">
              Airfare per adult:
              <b>R{{ Math.ceil(priceResponse.fareBreakdown.adults.baseFare / priceResponse.travellers.adults) }}</b>
            </span>
            <span class="price-item" v-if="priceResponse.fareBreakdown.children">
              Airfare per child:
              <b>
                R{{
                Math.ceil(priceResponse.fareBreakdown.children.baseFare / priceResponse.travellers.children)
                }}
              </b>
            </span>
            <span class="price-item" v-if="priceResponse.fareBreakdown.infants">
              Airfare per infant:
              <b>
                R{{
                Math.ceil(priceResponse.fareBreakdown.infants.baseFare / priceResponse.travellers.infants)
                }}
              </b>
            </span>
            <span class="price-item">
              Total airport taxes:
              <b>R{{ Math.ceil(priceResponse.fareBreakdown.taxAmount) }}</b>
            </span>
            <span class="price-item" v-if="productsCost > 0">
              Total extras:
              <b>
                R{{
                Math.ceil(productsCost)
                }}
              </b>
            </span>
            <span class="price-item" v-if="paymentCharge > 0">
              Payment option fee:
              <b>
                R{{
                Math.ceil(paymentCharge)
                }}
              </b>
            </span>
            <span class="price-item" v-if="baggageCost > 0">
              Total baggage cost:
              <b>
                R{{
                Math.ceil(baggageCost)
                }}
              </b>
            </span>
            <span class="price-item">
              Total price:
              <b>R{{ Math.ceil(priceResponse.totalAmount + productsCost + (baggageCost > 0 ? baggageCost : 0) + paymentCharge) }}</b>
            </span>
          </b-message>
          <h1 class="flights">Itinerary breakdown</h1>
          <div class="review-content" v-for="flight in allFlights" :key="flight.index">
            <b>Flight:</b>
            {{ searchResults.airlineNames[flight.airlineCode] }}
            {{ flight.flightNumber }}
            <span
              class="dulled-text"
              v-if="flight.opAirlineCode"
            >- operated by {{ flight.opAirlineCode.toLowerCase() }}</span>
            <br />
            <b>Depart:</b>
            {{ formatDate(flight.departureDateTime) }} from ({{
            flight.origCode
            }}) {{ determineLocationFromCode(flight.origCode).airport }}
            <br />
            <b>Arrive:</b>
            {{ formatDate(flight.arrivalDateTime) }} at ({{ flight.destCode }})
            {{ determineLocationFromCode(flight.destCode).airport }},
            <br />
            <b>Cabin:</b>
            {{ flight.cabinClass.toLowerCase() }}
          </div>
        </div>
        <b-button type="is-warning" outlined @click="backToResults">
          Back to results
        </b-button>
      </b-tab-item>

      <b-tab-item label="Bags">
        <b-message v-for="(leg, index) in selectedFlights.outbound.odoList" :key="'leg-' + index">
          <b
            class="flight"
          >Baggage {{leg.segments[0].origCode}} to {{leg.segments[leg.segments.length - 1].destCode}}:</b>
          <br />
          <span v-for="(bag, index) in leg.segments[0].baggage" :key="'bag-' + index">
            <span
              v-if="bag.type=='CHECKED' && !bag.name.includes('No') && !bag.name.includes('+')"
            >{{bag.name}} checked baggage</span>
            <span
              v-else-if="bag.name.includes('+')"
            >{{bag.name}} available at passenger details step</span>
            <span v-else-if="bag.name.includes('No')">No checked baggage</span>
            <span v-else>{{bag.name}}</span>
            <br />
          </span>
        </b-message>
        <div v-if="!isEmpty(selectedFlights.return)">
          <b-message v-for="(leg, index) in selectedFlights.return.odoList" :key="'leg-' + index">
            <b
              class="flight"
            >Baggage {{leg.segments[0].origCode}} to {{leg.segments[leg.segments.length - 1].destCode}}:</b>
            <br />
            <span v-for="(bag, index) in leg.segments[0].baggage" :key="'bag-' + index">
              <span
                v-if="bag.type=='CHECKED' && !bag.name.includes('No') && !bag.name.includes('+')"
              >{{bag.name}} checked baggage</span>
              <span
                v-else-if="bag.name.includes('+')"
              >{{bag.name}} available at passenger details step</span>
              <span v-else-if="bag.name.includes('No')">No checked baggage</span>
              <span v-else>{{bag.name}}</span>
              <br />
            </span>
          </b-message>
        </div>
      </b-tab-item>

      <b-tab-item label="Optional extras">
        <b-message
          type="is-warning"
          v-if="currentBlock > 2"
        >Optional extras can no longer be modified, go back to alter your selection.</b-message>
        <div class="air-rules">
          <div v-if="products">
            <div class="block products-list">
              <div
                class="margin-bot"
                v-for="(product, index) in priceResponse.products"
                :key="'product-' + index"
              >
                <div class="width-100">
                  <b-checkbox
                    :disabled="currentBlock > 2 ? true : false"
                    v-model="selectedProducts"
                    :native-value="product.id"
                  >
                    <b>{{ product.name }}:</b>
                  </b-checkbox>
                  <b class="pushed-righ">R{{ product.amount }}</b>
                </div>
                <div class="product-description">{{ product.description }}</div>
                <b-message
                  class="product-warning"
                  type="is-danger"
                  v-if="
                      !selectedProducts.includes(product.id) &&
                        product.warningText
                    "
                  v-html="product.warningText"
                ></b-message>
              </div>
            </div>
          </div>
        </div>
        <b-button type="is-warning" outlined @click="backToResults">
          Back to results
        </b-button>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";
import FadeLoader from "vue-spinner/src/FadeLoader.vue";

export default {
  name: "tripReview",
  data: function() {
    return {
      fareBreakdownIsOpen: false,
      passengers: [],
      selectedBags: [],
      selectedProducts: [],
      activeTab: 0,
      priceCheckResult: undefined,
      priceCheckComplete: false
    };
  },
  props: ["errors", "validationError"],
  components: {
    FadeLoader
  },
  computed: {
    ...mapState([
      "searchResults",
      "priceResponse",
      "selectedFlights",
      "products",
      "bags",
      "baggageCost",
      "currentBlock",
      "paymentCharge"
    ]),
    productsCost() {
      let result = 0;
      this.selectedProducts.forEach(selected => {
        result += this.priceResponse.products.find(e => e.id == selected)
          .amount;
      });
      return result;
    },
    // searchParams() {
    //   return this.flags.isUrlSearch
    //     ? this.urlSearchParameters
    //     : this.searchParameters;
    // },
    allFlights() {
      let flights = [];
      for (let itinerary of this.priceResponse.itineraries) {
        for (let leg of itinerary.odoList) {
          for (let segment of leg.segments) {
            segment.specialAttributes = itinerary.specialServiceAttributes;
            flights.push(segment);
          }
        }
      }
      return flights;
    }
  },
  methods: {
    backToResults() {
      this.$store.commit("updateBlock", 1);
      this.$router.go(-1);
    },
    isEmpty(obj) {
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) return false;
      }
      return true;
    },
    determineLocationFromCode(code) {
      return this.searchResults.airportInfos.find(e => {
        return e.iataCode == code;
      });
    },
    async setSelected() {
      this.priceResponse.baggageOptions.allBaggage.forEach(bag => {
        this.selectedBags.push(bag.id);
      });
      if (this.priceResponse.products) {
        this.products.forEach(product => {
          if (product.selectedValue) {
            this.selectedProducts.push(product.id);
          }
        });
      }
    },
    formatDate(date) {
      return moment(date).format("h:mm a MMMM Do");
    },
    updateProducts() {
      this.$store.commit("updateProducts", this.selectedProducts);
    }
  },
  mounted() {
    this.setSelected();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/settings.scss";

.rule-format {
  display: flex;
  flex-direction: column;
}

.review-content {
  border-radius: 4px;
  color: #4a4a4a;
  background: #f4f2f2;
  padding: 1.25em 1.5em;
  margin-bottom: 0.75em;
}

.width-100 {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.products-list {
  display: flex;
  flex-direction: column;
}

.product-description {
  font-size: 0.8em;
  margin-top: 0.5em;
}

.product-warning {
  padding: 0.75em;
  background-color: #ffe0e6 !important;
}

.margin-bot {
  margin-bottom: 1.2em;
}

.rule-heading {
  font-size: 1.1em;
  font-weight: bold;
  margin-right: 0.75em;
}

.heading-format {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.price-item {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.option-header {
  font-weight: bold;
  font-size: 1.1em;
}

.trip-review {
  align-self: start;
  background: $light;
  margin: 0 auto;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(32, 33, 36, 0.28);
}

.air-rules {
  img {
    max-width: 100px;
    margin-right: 0.75em;
  }
  display: flex;
  flex-direction: column;
}

.rules-loader-wrapper {
  width: 100%;
  height: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
}

.flight-info {
  display: flex;
  flex-direction: column;
}

.return-baggage {
  margin-top: 0.5em;
}

.message-body {
  padding: 1em;
}

.loading-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
}

.prompt-header {
  font-size: 1.5em;
  margin-top: 1rem;
  padding: 1rem;
}

.prompt-loader {
  margin-top: 3rem;
}

.dulled-text {
  font-size: 0.8em;
  font-style: italic;
}

.inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  margin-bottom: 1em;
}

.no-margin-bottom {
  margin-bottom: 0 !important;
}

.progress-buttons {
  display: flex;
  width: 100%;
  position: sticky;
  bottom: 0;
  left: 0;
  padding: 0.75em;
  background: white;
  justify-content: flex-end;
  align-items: center;
}

.margin-left-auto {
  margin-top: 0.35em;
}

.margin-right {
  margin-right: 0.5em;
  color: white;
}

@media (min-width: 781px) {
  // .inline {
  //     flex-direction: row;
  //     flex-wrap: nowrap;
  //     align-items: center;
  //     justify-content: flex-start;
  // }
}
</style>

<style lang="scss">
.message-header + .message-body {
  padding-top: 0 !important;
}

.message-header {
  p {
    color: white;
  }
}

.mango-and-radixx {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
}

.rule {
  padding: 0 0 1em 0;
}

.modal .animation-content {
  display: flex;
  max-height: 90%;
}

.my-button {
  font-size: 0.8em !important;
}

@media (min-width: 781px) {
  .my-button {
    font-size: 1rem !important;
  }
}
</style>
